/**
 * Metric contract of new report definitions (version 1), shared by Talk (rendering, editor, live preview) and the
 * API (metrics snapshots). Layout, components and tables of a definition are Talk-only and live in Talk.
 */

/** Question types the metric pipeline distinguishes; values match the GraphQL `QuestionType` enum. */
export const REPORT_QUESTION_TYPE = {
  Select: 'SELECT',
  Multiselect: 'MULTISELECT',
  Scale: 'SCALE',
  Freetext: 'FREETEXT',
} as const;

/** The question fields metric calculation and validation read; Talk's `GetNewReport` questions satisfy it. */
export interface MetricQuestion {
  index: string;
  type: string;
  answersLocal?: readonly string[] | null;
  statementsLocal?: readonly string[] | null;
  selectedRange?: string | null;
}

/** One answer of a response; `value` holds comma-separated option indexes, negative values are escapes. */
export interface MetricMessage {
  id: string;
  questionDefinitionIndex: string;
  statementIndex?: number | null;
  value?: string | null;
  text?: string | null;
}

/** One responding channel (questionnaire). */
export interface MetricResponse {
  id: string;
  respondentId: string | number;
  messages?: readonly MetricMessage[] | null;
}

export type LocalizedText = Record<string, string>;
/** `questionType` is an editor hint holding a GraphQL `QuestionType` value; calculations never read it. */
export type QuestionBinding = { questionIndex: string; statementIndex?: number; questionType?: string };
export type ScaleSource = QuestionBinding & { optionScores?: (number | null)[] };
export type ScoreSource = QuestionBinding & {
  optionScores: (number | null)[];
  min: number;
  max: number;
  rangeMode?: 'mapped' | 'custom';
};
export type Normalization = 'none' | 'range';
export type MetricCalculation =
  | { type: 'average'; sources: ScoreSource[]; normalization: Normalization }
  | { type: 'esi'; sources: ScoreSource[]; normalization: Normalization }
  | { type: 'nps'; source: ScoreSource; inputMin: 0 | 1 }
  | { type: 'compositeAverage'; metricIds: string[]; normalization: Normalization }
  | { type: 'choiceMatchRate'; sources: (QuestionBinding & { optionIndexes: number[] })[]; complement: boolean }
  /**
   * Average of all valid scale answers pooled across the cohort. This is not an average of per-respondent
   * averages or category metrics. `metricIds` reference other `scaleTotal` metrics whose underlying questions
   * are merged and deduplicated into this one.
   */
  | { type: 'scaleTotal'; sources: ScaleSource[]; metricIds?: string[]; normalization: Normalization }
  /**
   * Per-respondent average over scale questions (option value = option count − index, so a four-point scale
   * yields 1–4, rounded to three decimals) classified into bands. `bands` are ordered best to worst; `from` is the
   * inclusive lower bound and the last band catches everything below the others. The metric value is the share
   * of respondents in `primaryBandId` (0–100). `metricIds` merge the questions of `scaleTotal` metrics like in
   * `scaleTotal`. Employee engagement is this calculation with the engaged/neutral/disengaged bands.
   */
  | { type: 'scaleBands'; sources: ScaleSource[]; metricIds?: string[]; bands: ScaleBand[]; primaryBandId: string }
  /**
   * Share of respondents meeting **all** `conditions` (0–100), e.g. attrition risk = below mean − 1 SD in two
   * categories and an eNPS detractor. Means and deviations are taken over the whole report so a respondent's
   * status does not change between team views; counts and the share are those of the current cohort. Only
   * respondents every condition can evaluate are counted.
   */
  | { type: 'respondentSegment'; conditions: RespondentCondition[]; labels: SegmentLabels };
export type ScaleBand = { id: string; label: LocalizedText; from: number };
export type NpsBucket = 'detractor' | 'passive' | 'promoter';
export type SegmentLabels = { matching: LocalizedText; other: LocalizedText };
export type RespondentCondition =
  /** Respondent's own average over the scale questions (three decimals) < mean − `deviations` × sample SD. */
  | {
      kind: 'belowDeviation';
      id: string;
      label: LocalizedText;
      sources: ScaleSource[];
      metricIds?: string[];
      deviations: number;
    }
  /** Respondent's NPS answer falls into one of `buckets` (0–6 detractor, 7–8 passive, 9–10 promoter). */
  | { kind: 'npsBucket'; id: string; source: ScoreSource; inputMin: 0 | 1; buckets: NpsBucket[] };
export const SEGMENT_BAND_IDS = { matching: 'matching', other: 'other' } as const;
export type MetricRenderer = 'score' | 'bar' | 'breakdown' | 'distribution';
export type MetricFormat = 'outOfMax' | 'rangePercent' | 'maxPercent' | 'number';
export interface ReportMetricDefinition {
  id: string;
  /** Short code (e.g. LED, TS, ENG) under which the metric is stored for trends and benchmarks; omitted when not stored. */
  identifier?: string;
  title: LocalizedText;
  description: LocalizedText;
  calculation: MetricCalculation;
  /** `breakdown` shows the metric's own value together with the values of the parts it is made of. */
  presentation: { renderer: MetricRenderer; format: MetricFormat };
}

/** Metrics another metric is calculated from, in definition order. */
export function metricDependencies(calculation: MetricCalculation): string[] {
  if (calculation.type === 'compositeAverage') {
    return calculation.metricIds;
  }
  if (calculation.type === 'scaleTotal' || calculation.type === 'scaleBands') {
    return calculation.metricIds ?? [];
  }
  if (calculation.type === 'respondentSegment') {
    return calculation.conditions.flatMap((condition) =>
      condition.kind === 'belowDeviation' ? condition.metricIds ?? [] : [],
    );
  }
  return [];
}

/** Lowest band bound: the worst band catches every respondent the better bands did not. */
export const BAND_FLOOR = 0;
/** What the calculation pipeline needs; presentation is applied by renderers, so changing it never recalculates. */
export type ReportMetricCalculation = Pick<ReportMetricDefinition, 'id' | 'calculation'>;

export const METRIC_IDENTIFIER_MAX_LENGTH = 10;
export const isMetricIdentifier = (value: unknown): value is string =>
  typeof value === 'string' && new RegExp(`^[A-Z0-9_]{1,${METRIC_IDENTIFIER_MAX_LENGTH}}$`).test(value);

/** Identifiers key stored trend/benchmark values, so every metric sharing one is reported. */
export function duplicateMetricIdentifiers(metrics: Pick<ReportMetricDefinition, 'id' | 'identifier'>[]): string[] {
  const owners = new Map<string, string[]>();
  metrics.forEach(({ id, identifier }) => {
    if (identifier) {
      owners.set(identifier, [...(owners.get(identifier) ?? []), id]);
    }
  });
  return [...owners.values()].filter((ids) => ids.length > 1).flat();
}
